// src/data/legalDocs.js

const currentDate = new Date().toLocaleDateString('en-US', { 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric' 
});

export const privacyPolicy = `
<p><strong>Last Updated: ${currentDate}</strong></p>

<h2>Introduction</h2>
<p>Singapore Airlines Flight Route Tracker ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application.</p>

<h2>Information We Collect</h2>

<h3>Information You Provide</h3>
<ul>
  <li><strong>Account Information</strong>: When you sign in with Google, we collect your name, email address, and profile picture.</li>
  <li><strong>Payment Information</strong>: Payment details are processed securely by Stripe. We do not store your credit card information.</li>
</ul>

<h3>Automatically Collected Information</h3>
<ul>
  <li><strong>Usage Data</strong>: We collect information about how you interact with our service, including pages viewed, features used, and time spent.</li>
  <li><strong>Device Information</strong>: Browser type, device type, IP address, and operating system.</li>
  <li><strong>Analytics</strong>: We use Vercel Analytics to understand how our service is used.</li>
</ul>

<h2>How We Use Your Information</h2>
<p>We use the information we collect to:</p>
<ul>
  <li>Provide, maintain, and improve our services</li>
  <li>Process your subscription payments</li>
  <li>Send you service-related communications</li>
  <li>Respond to your requests and provide customer support</li>
  <li>Monitor and analyze usage patterns</li>
  <li>Detect and prevent fraud or abuse</li>
</ul>

<h2>Third-Party Services</h2>
<p>We use the following third-party services:</p>

<h3>Firebase (Google)</h3>
<ul>
  <li>Authentication and user management</li>
  <li>Database storage (Firestore)</li>
  <li>Privacy Policy: <a href="https://firebase.google.com/support/privacy" target="_blank">firebase.google.com/support/privacy</a></li>
</ul>

<h3>Stripe</h3>
<ul>
  <li>Payment processing and subscription management</li>
  <li>Privacy Policy: <a href="https://stripe.com/privacy" target="_blank">stripe.com/privacy</a></li>
</ul>

<h3>Vercel Analytics</h3>
<ul>
  <li>Website analytics and performance monitoring</li>
  <li>Privacy Policy: <a href="https://vercel.com/legal/privacy-policy" target="_blank">vercel.com/legal/privacy-policy</a></li>
</ul>

<h2>Your Rights</h2>
<p>You have the right to:</p>
<ul>
  <li>Access your personal data</li>
  <li>Correct inaccurate data</li>
  <li>Request deletion of your data</li>
  <li>Export your data</li>
  <li>Opt-out of marketing communications</li>
  <li>Withdraw consent at any time</li>
</ul>

<p>To exercise these rights, contact us at: <a href="mailto:hypacharge@gmail.com">hypacharge@gmail.com</a></p>

<h2>Data Security</h2>
<ul>
  <li>Your data is stored securely using Firebase (Google Cloud Platform)</li>
  <li>Payment processing is handled by Stripe, a PCI-compliant payment processor</li>
  <li>We use industry-standard encryption for data transmission</li>
</ul>

<h2>Children's Privacy</h2>
<p>Our service is not intended for users under 13 years of age. We do not knowingly collect personal information from children under 13.</p>

<h2>Contact Us</h2>
<p>If you have questions about this Privacy Policy:</p>
<ul>
  <li>Email: <a href="mailto:hypacharge@gmail.com">hypacharge@gmail.com</a></li>
  <li>Website: <a href="https://sia-flight-tracker.vercel.app">sia-flight-tracker.vercel.app</a></li>
</ul>
`;

export const termsOfService = `
<p><strong>Last Updated: ${currentDate}</strong></p>

<h2>Agreement to Terms</h2>
<p>By accessing or using Singapore Airlines Flight Route Tracker ("Service"), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the Service.</p>

<h2>Subscription Plans</h2>

<h3>Free Tier</h3>
<ul>
  <li>Access to basic flight tracking features</li>
  <li>Limited to 5-day historical data</li>
  <li>Standard map view</li>
</ul>

<h3>Premium Tier ($7.99/month)</h3>
<ul>
  <li>Full access to all features</li>
  <li>10-day historical data</li>
  <li>Advanced analytics and filters</li>
  <li>Priority support</li>
</ul>

<h2>Payment Terms</h2>
<ul>
  <li>Subscription fees are billed monthly in advance</li>
  <li>All fees are in USD and non-refundable except as required by law</li>
  <li>Payment processing is handled securely by Stripe</li>
  <li>You may cancel your subscription at any time</li>
  <li>Cancellations take effect at the end of the current billing period</li>
</ul>

<h2>Acceptable Use</h2>
<p>You agree NOT to:</p>
<ul>
  <li>Use the Service for any illegal purpose</li>
  <li>Attempt to gain unauthorized access to the Service</li>
  <li>Interfere with or disrupt the Service</li>
  <li>Use automated scripts without permission</li>
  <li>Resell or redistribute data from the Service</li>
</ul>

<h2>Data Accuracy</h2>
<ul>
  <li>Flight information is provided "as is"</li>
  <li>We strive for accuracy but cannot guarantee real-time precision</li>
  <li>We are not affiliated with Singapore Airlines</li>
  <li>Use flight information for informational purposes only</li>
</ul>

<h2>Limitation of Liability</h2>
<p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
<ul>
  <li>The Service is provided "AS IS" without warranties</li>
  <li>We are not liable for indirect, incidental, or consequential damages</li>
  <li>Our total liability is limited to the amount you paid in the last 12 months</li>
</ul>

<h2>Account Termination</h2>
<p>We may terminate or suspend your account if you:</p>
<ul>
  <li>Violate these Terms</li>
  <li>Engage in fraudulent activity</li>
  <li>Abuse the Service or other users</li>
</ul>

<h2>Disclaimer</h2>
<ul>
  <li>We are not affiliated with Singapore Airlines</li>
  <li>This is an independent tracking service</li>
  <li>All airline trademarks are property of Singapore Airlines</li>
</ul>

<h2>Contact Information</h2>
<p>For questions about these Terms:</p>
<ul>
  <li>Email: <a href="mailto:hypacharge@gmail.com">hypacharge@gmail.com</a></li>
  <li>Website: <a href="https://sia-flight-tracker.vercel.app">sia-flight-tracker.vercel.app</a></li>
</ul>
`;

export const refundPolicy = `
<p><strong>Last Updated: ${currentDate}</strong></p>

<h2>General Policy</h2>
<p><strong>Subscription fees are generally non-refundable.</strong> By subscribing, you agree that you will not be entitled to a refund except as outlined below.</p>

<h2>Eligible Refund Situations</h2>
<p>We may issue refunds at our sole discretion in the following circumstances:</p>

<h3>1. Technical Issues</h3>
<ul>
  <li>Service is completely inaccessible due to errors on our end</li>
  <li>Issues cannot be resolved within 48 hours</li>
  <li>Problems are documented and reported to support</li>
</ul>

<h3>2. Billing Errors</h3>
<ul>
  <li>Duplicate charges for the same subscription period</li>
  <li>Charges after confirmed cancellation</li>
  <li>Unauthorized charges</li>
</ul>

<h3>3. First-Time Subscribers</h3>
<ul>
  <li>Cancel within 48 hours of first subscription</li>
  <li>Must not have extensively used premium features</li>
  <li>Limited to one-time courtesy refund per user</li>
</ul>

<h2>Non-Refundable Situations</h2>
<p>Refunds will NOT be issued for:</p>
<ul>
  <li>Change of mind after 48 hours</li>
  <li>Failure to use the Service</li>
  <li>Failure to cancel before renewal</li>
  <li>Account termination due to violations</li>
  <li>Partial month subscriptions</li>
</ul>

<h2>Refund Request Process</h2>
<ol>
  <li><strong>Contact Support</strong>: Email <a href="mailto:hypacharge@gmail.com">hypacharge@gmail.com</a></li>
  <li><strong>Subject</strong>: "Refund Request - [Your Email]"</li>
  <li><strong>Include</strong>: Transaction ID, reason, date of charge</li>
  <li><strong>Review Period</strong>: 5 business days</li>
  <li><strong>Processing</strong>: 5-10 business days if approved</li>
</ol>

<h2>Cancellation vs. Refund</h2>
<ul>
  <li><strong>Cancellation</strong>: Stops future billing, retains access until period end, no refund</li>
  <li><strong>Refund</strong>: Returns money, access may be immediately revoked</li>
</ul>

<h2>Subscription Management</h2>
<p>Instead of requesting a refund:</p>
<ul>
  <li><strong>Downgrade</strong>: Switch to free tier</li>
  <li><strong>Cancel</strong>: Stop future billing, keep access until period end</li>
  <li><strong>Manage</strong>: Via Stripe Customer Portal in your account menu</li>
</ul>

<h2>Contact Us</h2>
<p>For refund requests or questions:</p>
<ul>
  <li><strong>Email</strong>: <a href="mailto:hypacharge@gmail.com">hypacharge@gmail.com</a></li>
  <li><strong>Response Time</strong>: Within 1-2 business days</li>
</ul>
`;